/**
 * The canonical registry: one identity per real thing, whatever a source system
 * happens to call it.
 *
 * Creating an entity is offered, on `entity.create` alone
 * (`NXP-P8-CONSOLE-V1-GAPS-001`): a steward has to be able to mint the record a
 * person is before anything can be linked or consented against it. Retiring,
 * merging and splitting are separately granted acts with their own evidence,
 * and this screen does not put an irreversible identity operation behind a
 * button before the screen that explains the consequence exists.
 *
 * What it does steward is **links** (`NXP-P8-STEWARD-UI-001`): which records in
 * which source systems a person has asserted are this entity. Three operations,
 * each asked about on its own — reading an entity's links, claiming one, and
 * deciding a proposed one — and nothing else. Resolving, candidates, import,
 * merge and split are platform operations this console deliberately does not
 * offer.
 */

import { useCallback, useMemo, useState } from "react";
import { api } from "../api/client";
import { NexusError } from "../api/errors";
import type {
  CanonicalEntity,
  CanonicalEntityType,
  ConnectedSystem,
  EntityLinkStatus,
  ExternalEntityLink,
  Page,
} from "../api/types";
import { usePagedCollection, type PagedCollection } from "../api/useCollection";
import { useResource } from "../api/useResource";
import {
  change,
  reasonedRefusal,
  RefusedRead,
  RequiredAuthority,
  Result,
  type Outcome,
} from "../components/governed";
import {
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  LowerBound,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * The capability keys the decision point is asked about, one per operation.
 *
 * Holding `read` is not evidence of `create`, and holding `create` is not
 * evidence of `confirm` — the platform decides them apart, and so does this
 * screen.
 */
const LINK_READ = "entity_link.read";
const LINK_CREATE = "entity_link.create";
const LINK_CONFIRM = "entity_link.confirm";
const SYSTEM_READ = "system.read";

/**
 * The match method a steward's own claim is recorded under.
 *
 * `manual` because that is what it is: a person asserting identity. The platform
 * treats a manual assertion as deterministic evidence and records it
 * **confirmed** at once — the steward's assertion *is* the decision. Only a
 * probabilistic assertion lands `proposed`, and calling a person's claim
 * probabilistic to make it wait would put a false method into the evidence.
 */
const CLAIM_METHOD = "manual";

const LINK_TONE: Record<EntityLinkStatus, Tone> = {
  proposed: "wait",
  confirmed: "ok",
  rejected: "bad",
  superseded: "quiet",
};

/** Personal linkage is PII: knowing two systems mean the same person is personal data. */
const PERSONAL_TYPES = new Set<string>(["person", "employee"]);

const LINK_PAGE = 50;

const TYPES: CanonicalEntityType[] = [
  "organization",
  "product",
  "application",
  "repository",
  "person",
];

/** The page size this screen asks for. Unchanged from before it paginated. */
const PAGE = 50;

const ENTITY_CREATE = "entity.create";

/** Every type the registry mints, in the order a steward is likeliest to want. */
const CREATABLE: CanonicalEntityType[] = [
  "person",
  "employee",
  "organization",
  "product",
  "facility",
  "site",
  "market",
  "application",
  "repository",
];

/**
 * The origin a console-created entity is recorded under.
 *
 * `nexus_core` is the catalogue's own entry for Nexus. The create contract has
 * no provenance kind, so an entity always names a catalogued system as where it
 * came from; one a steward mints here came from Nexus itself, and that is the
 * true statement. A registered connected system is offered alongside it for an
 * identity a steward is transcribing from one.
 */
const NEXUS_ORIGIN = "nexus_core";

/**
 * One question asked of the registry, read a page at a time.
 *
 * `projection` is thrown away here, and the proof that nothing is lost by it is
 * worth writing down because the next reader will ask. `/core/entities` answers
 * with `Page.of(...)` — `src/nexus/core/registry/service.py:458` — and `Page.of`
 * (`src/nexus/core/contracts/api.py:212-231`) takes no `projection` argument and
 * never passes one, so the field keeps its `None` default
 * (`contracts/api.py:209-210`). The only code in the platform that ever
 * constructs a non-null projection is the outbox service
 * (`src/nexus/core/outbox/service.py:222`), which serves `/core/events/outbox`
 * and not this route. So `projection` is null on every page this screen reads,
 * and `PageOf` — which does not carry it — drops nothing. If the registry ever
 * starts answering for its own completeness, this is the line that would have to
 * stop discarding it.
 *
 * `resetKey` is the question, not the endpoint. A different type or a different
 * search term is a different collection: continuing it from the previous
 * question's cursor would answer the new question from the old one's position,
 * and appending its rows would file the answers to one question under another.
 */
function useEntities(type: string, query: string): PagedCollection<CanonicalEntity> {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<CanonicalEntity>>("/core/entities", {
        params: {
          limit: PAGE,
          cursor,
          ...(type ? { entity_type: type } : {}),
          ...(query ? { q: query } : {}),
        },
        signal,
      }),
    [type, query],
  );
  // Two values that can each contain the other's separator would collide as a
  // key, so the question is serialised rather than joined.
  const resetKey = useMemo(() => JSON.stringify([type, query]), [type, query]);
  return usePagedCollection<CanonicalEntity>(readPage, { resetKey });
}

export function Entities() {
  const [type, setType] = useState<string>("");
  const session = useSession();
  // The entity whose detail is open. Held as the entity rather than an id,
  // because one just created may not be on any page read so far.
  const [open, setOpen] = useState<CanonicalEntity | null>(null);
  const openId = open?.entity_id ?? null;
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");

  const entities = useEntities(type, query);

  // Only once the registry has actually answered. A count rendered while the
  // first page is still in flight would say "0" about something nobody has
  // been told yet, which is the opposite of what a lower bound is for.
  const answered = entities.state.kind === "ready" || entities.items.length > 0;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <Card
        title={
          <span>
            Canonical entities
            {answered ? (
              <span className="nx-hint" style={{ fontWeight: 400 }}>
                {" · "}
                {/*
                  What the platform said was "here are as many as you asked for,
                  and there are more" — so the honest rendering of the rows read
                  so far is a bound, not a total.
                */}
                <LowerBound count={entities.items.length} hasMore={entities.hasMore} />
              </span>
            ) : null}
          </span>
        }
        actions={
          <form
            style={{ display: "flex", gap: 8 }}
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(term);
            }}
          >
            <label className="nx-visually-hidden" htmlFor="entity-type">
              Entity type
            </label>
            <select
              id="entity-type"
              className="nx-select"
              value={type}
              onChange={(event) => {
                setType(event.target.value);
              }}
            >
              <option value="">All types</option>
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <label className="nx-visually-hidden" htmlFor="entity-search">
              Search entities
            </label>
            <input
              id="entity-search"
              className="nx-input"
              placeholder="Search by name"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
              }}
            />
            <button type="submit" className="nx-button">
              Search
            </button>
          </form>
        }
        bodyless
      >
        <Show
          resource={entities.state}
          what="the canonical registry"
          onRetry={entities.refresh}
          empty={() =>
            entities.items.length === 0 ? (
              <Empty title="Nothing matches." hint="Try another type, or a different name." />
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">Canonical entities in this tenant</caption>
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Type</th>
                    <th scope="col">Status</th>
                    <th scope="col">Scope</th>
                    <th scope="col">Updated</th>
                    <th scope="col">
                      <span className="nx-visually-hidden">Detail</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entities.items.map((entity) => (
                    <tr key={entity.entity_id}>
                      <td>
                        <span className="nx-table__primary">{entity.canonical_name}</span>
                        <span className="nx-table__secondary" style={{ display: "block" }}>
                          <Identifier value={entity.entity_id} />
                        </span>
                      </td>
                      <td>{entity.entity_type}</td>
                      <td>
                        <Pill tone={entity.status === "active" ? "ok" : "quiet"}>{entity.status}</Pill>
                      </td>
                      <td>{entity.scope}</td>
                      <td>{when(entity.updated_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="nx-button"
                          aria-pressed={openId === entity.entity_id}
                          aria-label={`Open ${entity.canonical_name}`}
                          onClick={() => {
                            setOpen(openId === entity.entity_id ? null : entity);
                          }}
                        >
                          {openId === entity.entity_id ? "Close" : "Open"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
        {/*
          The rest of the registry, and — more importantly — that there is a rest.
          The sentence that used to stand here told the reader to narrow the
          search, which was the screen admitting it could not go on. It can.
        */}
        <More
          collection={entities}
          label="Show more entities"
          hint="This is one page of the registry, not all of it."
          placement="bodyless"
        />
      </Card>
      {session.may(ENTITY_CREATE) ? (
        <CreateEntity
          onCreated={(created) => {
            setOpen(created);
            entities.refresh();
          }}
        />
      ) : null}
      {open === null ? null : <EntityDetail key={open.entity_id} entity={open} />}
    </div>
  );
}

// ── one entity ────────────────────────────────────────────────────────────

/**
 * One entity, and the source-system records a steward has said it is.
 *
 * The outcome of a change is held here rather than in the section that made
 * it, because a change refreshes the links and whatever was open re-renders
 * while that happens — the confirmation and its link to the evidence must
 * survive exactly that moment.
 */
function EntityDetail({ entity }: { entity: CanonicalEntity }) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  return (
    <Card title={entity.canonical_name}>
      <Facts>
        <Fact label="Identifier">
          <Mono>{entity.entity_id}</Mono>
        </Fact>
        <Fact label="Type">{entity.entity_type}</Fact>
        <Fact label="Status">{entity.status}</Fact>
        <Fact label="Scope">{entity.scope}</Fact>
      </Facts>
      <SourceLinks entity={entity} onOutcome={setOutcome} />
      <Result outcome={outcome} />
    </Card>
  );
}

function SourceLinks({
  entity,
  onOutcome,
}: {
  entity: CanonicalEntity;
  onOutcome: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const personal = PERSONAL_TYPES.has(entity.entity_type);

  return (
    <section aria-labelledby="source-links-title" style={{ marginTop: 18 }}>
      <h3 id="source-links-title" style={{ fontSize: "var(--nx-text-base)", margin: "0 0 8px" }}>
        Source-system links
      </h3>
      <p className="nx-hint" style={{ margin: "0 0 8px" }}>
        A link is a human steward&apos;s assertion that a record in a source system is this entity.
        Nexus copies nothing from that record — it holds only the claim, who made it, and when.
        Applications only ever read <strong>confirmed</strong> links: a proposed link changes
        nothing for them until a steward confirms it, and a rejected one stays on record as refused.
      </p>
      {personal ? (
        <p className="nx-hint" style={{ margin: "0 0 8px" }}>
          <Classification value="pii" /> Knowing that two systems hold the same person is personal
          data, so every link of this entity is decided at that classification — an access policy a
          data owner activates is needed as well as a role.
        </p>
      ) : null}
      {session.may(LINK_READ) ? (
        <LinkList entity={entity} onOutcome={onOutcome} />
      ) : (
        <RequiredAuthority capability={LINK_READ} what="Reading this entity's links" />
      )}
    </section>
  );
}

function LinkList({
  entity,
  onOutcome,
}: {
  entity: CanonicalEntity;
  onOutcome: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ExternalEntityLink>>(`/core/entities/${entity.entity_id}/links`, {
        params: { limit: LINK_PAGE, cursor },
        signal,
      }),
    [entity.entity_id],
  );
  const links = usePagedCollection<ExternalEntityLink>(readPage, { resetKey: entity.entity_id });

  const changed = (result: Outcome) => {
    onOutcome(result);
    if (result.tone === "ok") links.refresh();
  };

  return (
    <>
      {links.state.kind === "forbidden" ? (
        <RefusedRead error={links.state.error} what="this entity's links" />
      ) : (
        <Show
          resource={links.state}
          what="this entity's links"
          onRetry={links.refresh}
          empty={() =>
            links.items.length === 0 ? (
              <Empty
                title="No source-system links."
                hint="No steward has claimed a source record as this entity."
              />
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">
                  Source-system records claimed as {entity.canonical_name}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Source record</th>
                    <th scope="col">Status</th>
                    <th scope="col">Asserted by</th>
                    <th scope="col">Since</th>
                    <th scope="col">
                      <span className="nx-visually-hidden">Decision</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {links.items.map((link) => (
                    <tr key={link.link_id}>
                      <td>
                        <span className="nx-table__primary">
                          {link.source.source_system} · {link.source.source_record_type}
                        </span>
                        <span className="nx-table__secondary" style={{ display: "block" }}>
                          <Mono>{link.source.source_record_id}</Mono>
                        </span>
                      </td>
                      <td>
                        <Pill tone={LINK_TONE[link.status]}>{link.status}</Pill>
                        <span className="nx-hint" style={{ display: "block" }}>
                          {link.match_method.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td>
                        {link.created_by}
                        {link.confirmed_by ? (
                          <span className="nx-hint" style={{ display: "block" }}>
                            confirmed by {link.confirmed_by}
                          </span>
                        ) : null}
                      </td>
                      <td>{when(link.effective_from)}</td>
                      <td>
                        {/*
                          Only a proposed link is waiting on a decision. A
                          confirmed one is what applications read, a rejected
                          one is the record of a refusal, and a superseded one
                          is history; none of them is decided again here.
                        */}
                        {link.status === "proposed" && session.may(LINK_CONFIRM) ? (
                          <LinkDecision link={link} onOutcome={changed} />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
      )}
      <More
        collection={links}
        label="Show more links"
        hint="This is one page of this entity's links, not all of them."
      />
      {links.items.some((link) => link.status === "proposed") && !session.may(LINK_CONFIRM) ? (
        <RequiredAuthority capability={LINK_CONFIRM} what="Confirming or rejecting a proposed link" />
      ) : null}
      {session.may(LINK_CREATE) ? (
        <ClaimLink entity={entity} onOutcome={changed} />
      ) : (
        <RequiredAuthority capability={LINK_CREATE} what="Claiming a source record as this entity" />
      )}
    </>
  );
}

function LinkDecision({
  link,
  onOutcome,
}: {
  link: ExternalEntityLink;
  onOutcome: (outcome: Outcome) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const record = `${link.source.source_system} ${link.source.source_record_type} ${link.source.source_record_id}`;

  const decide = (verb: "confirm" | "reject") => {
    setBusy(true);
    void change(
      async (correlationId) => {
        // Neither route implements `Idempotency-Key`; confirming twice is
        // answered by the platform, not by a replay.
        await api.post<ExternalEntityLink>(
          `/core/links/${link.link_id}/${verb}`,
          verb === "reject" ? { note: note.trim() === "" ? null : note.trim() } : {},
          { correlationId },
        );
      },
      verb === "confirm" ? "Confirming this link" : "Rejecting this link",
      verb === "confirm"
        ? `Confirmed: ${record} is this entity. Applications reading confirmed links now see it.`
        : `Rejected: ${record} is recorded as not this entity.`,
      reasonedRefusal,
    )
      .then(onOutcome)
      .finally(() => {
        setBusy(false);
      });
  };

  if (!rejecting) {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="nx-button nx-button--primary"
          disabled={busy}
          aria-label={`Confirm ${record}`}
          onClick={() => {
            decide("confirm");
          }}
        >
          Confirm
        </button>
        <button
          type="button"
          className="nx-button"
          disabled={busy}
          aria-label={`Reject ${record}`}
          onClick={() => {
            setRejecting(true);
          }}
        >
          Reject
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        decide("reject");
      }}
    >
      <div className="nx-field">
        <label htmlFor={`reject-note-${link.link_id}`}>Why (optional)</label>
        <input
          id={`reject-note-${link.link_id}`}
          className="nx-input"
          maxLength={512}
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
          Reject link
        </button>
        <button
          type="button"
          className="nx-button"
          onClick={() => {
            setRejecting(false);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Keys the platform accepts for a source system or record type. */
const KEY_PATTERN = "[a-z][a-z0-9_]*";

/**
 * The systems a link may point into: the governed catalogue, linkable ones only.
 *
 * Read only when the caller may read the registry. Otherwise — or if the read is
 * refused — the key is typed, and the platform checks it against the same
 * catalogue when the claim arrives.
 */
function useLinkableSystems(enabled: boolean) {
  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<Page<ConnectedSystem>>("/core/connected-systems", { params: { limit: 200 }, signal }),
    [],
  );
  const { state } = useResource<Page<ConnectedSystem>>(read, { enabled });
  if (!enabled || state.kind !== "ready") return null;
  return state.value.items.filter((system) => system.linkable);
}

function ClaimLink({
  entity,
  onOutcome,
}: {
  entity: CanonicalEntity;
  onOutcome: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const systems = useLinkableSystems(session.may(SYSTEM_READ));
  const [form, setForm] = useState({ system: "", recordType: "", recordId: "", evidence: "" });
  const [busy, setBusy] = useState(false);

  return (
    <form
      aria-labelledby="claim-link-title"
      style={{ marginTop: 14 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        const claimed: { value?: ExternalEntityLink } = {};
        void change(
          async (correlationId) => {
            // Exactly the fields the API declares. The route accepts an
            // `Idempotency-Key`, so a retry after a timeout replays instead of
            // asserting twice.
            claimed.value = await api.post<ExternalEntityLink>(
              "/core/links",
              {
                source: {
                  source_system: form.system,
                  source_record_type: form.recordType,
                  source_record_id: form.recordId,
                },
                canonical_entity_id: entity.entity_id,
                match_method: CLAIM_METHOD,
                ...(form.evidence.trim() === "" ? {} : { evidence_ref: form.evidence.trim() }),
              },
              { correlationId, idempotent: true },
            );
          },
          "Claiming this link",
          `Claimed ${form.system} ${form.recordType} ${form.recordId} as ${entity.canonical_name}.`,
          reasonedRefusal,
        )
          .then((result) => {
            // Whatever the platform recorded, said in its own words: the status
            // is the platform's answer, not this screen's expectation.
            const status = claimed.value?.status;
            onOutcome(
              result.tone === "ok" && status
                ? { ...result, message: `${result.message} Nexus recorded it as ${status}.` }
                : result,
            );
            if (result.tone === "ok") {
              setForm({ system: form.system, recordType: "", recordId: "", evidence: "" });
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h4 id="claim-link-title" style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>
        Claim a source record as this entity
      </h4>
      <div className="nx-field">
        <label htmlFor="claim-system">Source system</label>
        {systems !== null && systems.length > 0 ? (
          <select
            id="claim-system"
            className="nx-select"
            required
            value={form.system}
            onChange={(event) => {
              setForm({ ...form, system: event.target.value });
            }}
          >
            <option value="">Choose a registered system</option>
            {systems.map((system) => (
              <option key={system.key} value={system.key}>
                {system.name} ({system.key})
              </option>
            ))}
          </select>
        ) : (
          <>
            <input
              id="claim-system"
              className="nx-input nx-mono"
              required
              maxLength={64}
              pattern={KEY_PATTERN}
              aria-describedby="claim-system-hint"
              value={form.system}
              onChange={(event) => {
                setForm({ ...form, system: event.target.value });
              }}
            />
            <span className="nx-hint" id="claim-system-hint">
              The registered key of a linkable connected system. Nexus refuses a key its catalogue
              does not hold.
            </span>
          </>
        )}
      </div>
      <div className="nx-field">
        <label htmlFor="claim-record-type">Source record type</label>
        <input
          id="claim-record-type"
          className="nx-input nx-mono"
          required
          maxLength={64}
          pattern={KEY_PATTERN}
          value={form.recordType}
          onChange={(event) => {
            setForm({ ...form, recordType: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="claim-record-id">Source record id</label>
        <input
          id="claim-record-id"
          className="nx-input nx-mono"
          required
          maxLength={255}
          value={form.recordId}
          onChange={(event) => {
            setForm({ ...form, recordId: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="claim-evidence">Evidence reference (optional)</label>
        <input
          id="claim-evidence"
          className="nx-input"
          maxLength={512}
          value={form.evidence}
          onChange={(event) => {
            setForm({ ...form, evidence: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Claim link
      </button>
      <p className="nx-hint" style={{ marginTop: 8 }}>
        You are asserting, as a steward, that this record is {entity.canonical_name}. It is recorded
        as a manual match under your name, and Nexus records a person&apos;s manual assertion as
        confirmed — so applications will read it. Only a probabilistic suggestion waits as
        proposed for a steward to confirm or reject.
      </p>
    </form>
  );
}

// ── minting ───────────────────────────────────────────────────────────────

/**
 * Create one tenant canonical entity (`POST /core/entities`).
 *
 * No `Idempotency-Key`: the route does not implement one, and sending it would
 * promise a replay nothing performs. What protects a retry is the registry
 * itself — a second active entity of the same type and name is refused with
 * `CONFLICT`, naming the one that already holds it — and that refusal is
 * rendered with the identifier, so the steward opens the existing record
 * instead of making a near-copy.
 */
function CreateEntity({ onCreated }: { onCreated: (entity: CanonicalEntity) => void }) {
  const session = useSession();
  const systems = useLinkableSystems(session.may(SYSTEM_READ));
  const [form, setForm] = useState({
    entity_type: "person" as CanonicalEntityType,
    canonical_name: "",
    source_system: NEXUS_ORIGIN,
    source_record_id: "",
    aliases: "",
  });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  return (
    <Card title="Create a canonical entity">
      <p className="nx-hint" style={{ marginTop: 0 }}>
        A canonical entity is the enterprise&apos;s record of who or what something is — one
        identity, whatever each source system calls it. Stewards create them; applications never
        do. They only read what is here, through confirmed links.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          const created: { value?: CanonicalEntity } = {};
          const aliases = form.aliases
            .split("\n")
            .map((alias) => alias.trim())
            .filter((alias) => alias !== "");
          void change(
            async (correlationId) => {
              // Exactly the fields the API declares; optional ones are left
              // out rather than sent empty.
              created.value = await api.post<CanonicalEntity>(
                "/core/entities",
                {
                  entity_type: form.entity_type,
                  canonical_name: form.canonical_name.trim(),
                  source_system: form.source_system,
                  match_method: "manual",
                  ...(form.source_record_id.trim() === ""
                    ? {}
                    : { source_record_id: form.source_record_id.trim() }),
                  ...(aliases.length === 0 ? {} : { aliases }),
                },
                { correlationId },
              );
            },
            "Creating an entity",
            "Created.",
            (error, what) => {
              const existing =
                error instanceof NexusError && error.code === "CONFLICT"
                  ? error.detail.entity_id
                  : undefined;
              return typeof existing === "string"
                ? `${error instanceof Error ? error.message : "Refused"} — the existing entity is ${existing}. Open it instead of creating a near-copy.`
                : reasonedRefusal(error, what);
            },
          )
            .then((result) => {
              const entity = created.value;
              if (result.tone === "ok" && entity !== undefined) {
                setOutcome({
                  ...result,
                  message: `Created ${entity.canonical_name} (${entity.entity_type}) as ${entity.entity_id}. Its detail is open below.`,
                });
                setForm({ ...form, canonical_name: "", source_record_id: "", aliases: "" });
                onCreated(entity);
              } else {
                setOutcome(result);
              }
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <div className="nx-field">
          <label htmlFor="create-type">Type</label>
          <select
            id="create-type"
            className="nx-select"
            value={form.entity_type}
            onChange={(event) => {
              setForm({ ...form, entity_type: event.target.value });
            }}
          >
            {CREATABLE.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="nx-field">
          <label htmlFor="create-name">Canonical name</label>
          <input
            id="create-name"
            className="nx-input"
            required
            maxLength={512}
            value={form.canonical_name}
            onChange={(event) => {
              setForm({ ...form, canonical_name: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="create-origin">Where this identity comes from</label>
          <select
            id="create-origin"
            className="nx-select"
            value={form.source_system}
            onChange={(event) => {
              setForm({ ...form, source_system: event.target.value });
            }}
          >
            <option value={NEXUS_ORIGIN}>Recorded here, in Nexus ({NEXUS_ORIGIN})</option>
            {(systems ?? []).map((system) => (
              <option key={system.key} value={system.key}>
                {system.name} ({system.key})
              </option>
            ))}
          </select>
        </div>
        <div className="nx-field">
          <label htmlFor="create-record">Source record id (optional)</label>
          <input
            id="create-record"
            className="nx-input nx-mono"
            maxLength={255}
            value={form.source_record_id}
            onChange={(event) => {
              setForm({ ...form, source_record_id: event.target.value });
            }}
          />
          <span className="nx-hint">
            Provenance only. To say a source record <em>is</em> this entity, claim a link from its
            detail once it exists.
          </span>
        </div>
        <div className="nx-field">
          <label htmlFor="create-aliases">Other names (optional, one per line)</label>
          <textarea
            id="create-aliases"
            className="nx-input"
            rows={2}
            value={form.aliases}
            onChange={(event) => {
              setForm({ ...form, aliases: event.target.value });
            }}
          />
        </div>
        <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
          Create entity
        </button>
      </form>
      <Result outcome={outcome} />
    </Card>
  );
}
